import { useMemo, useState } from "react";
import {
  iBondPurchaseSchedule,
  projectIBondValue,
} from "../../lib/util/i-bonds.js";
import {
  Button,
  Card,
  SectionHeader,
  TextField,
} from "../../components/design-system/index.js";

/**
 * /banking/i-bonds — Phase 8 Session 3.
 *
 * I-Bond purchase scheduler. Treasury Direct caps electronic purchases
 * at $10,000/SSN/calendar-year. The helper enforces the cap, returns a
 * year-by-year schedule, and projects accumulated value at a horizon.
 *
 * Treasury Direct deep-link surfaces at the top — the user has to
 * actually buy them at TD; Hearth only plans the schedule.
 */

export function BankingIBondsPage() {
  const [targetDollars, setTargetDollars] = useState<string>("50000");
  const [numAdults, setNumAdults] = useState<string>("2");
  const [compositeRatePct, setCompositeRatePct] = useState<string>("4.30");
  const [horizonYears, setHorizonYears] = useState<string>("10");

  const result = useMemo(() => {
    const target = Math.round(Number.parseFloat(targetDollars) * 100);
    const adults = Math.max(1, Number.parseInt(numAdults, 10) || 1);
    if (!Number.isFinite(target) || target <= 0) return null;
    return iBondPurchaseSchedule({
      target_total_cents: target,
      num_adults: adults,
    });
  }, [targetDollars, numAdults]);

  const projected = useMemo(() => {
    if (!result) return null;
    const ratePct = Number.parseFloat(compositeRatePct);
    const horizonY = Number.parseInt(horizonYears, 10) || 0;
    if (!Number.isFinite(ratePct) || horizonY <= 0) return null;
    const startYear = result.rungs[0]?.year ?? new Date().getUTCFullYear();
    return projectIBondValue({
      schedule: result,
      composite_rate_bp: Math.round(ratePct * 100),
      horizon_year: startYear + horizonY,
    });
  }, [result, compositeRatePct, horizonYears]);

  return (
    <div className="mx-auto max-w-4xl p-6">
      <SectionHeader
        title="I-Bond purchase scheduler"
        sub="Treasury Direct caps electronic purchases at $10,000 per SSN per calendar year. Hearth plans the multi-year schedule; you buy at Treasury Direct."
        action={
          <Button
            type="button"
            onClick={() =>
              window.open(
                "https://www.treasurydirect.gov/",
                "_blank",
                "noopener,noreferrer",
              )
            }
          >
            Open Treasury Direct ↗
          </Button>
        }
      />
      <Card className="mb-4">
        <div className="flex flex-wrap gap-3">
          <TextField
            label="Target total ($)"
            value={targetDollars}
            onChange={(e) => setTargetDollars(e.target.value)}
            type="number"
            min={0}
          />
          <TextField
            label="# Adults (SSNs)"
            value={numAdults}
            onChange={(e) => setNumAdults(e.target.value)}
            type="number"
            min={1}
          />
          <TextField
            label="Composite rate (%)"
            value={compositeRatePct}
            onChange={(e) => setCompositeRatePct(e.target.value)}
            type="number"
            step="0.01"
            min={0}
          />
          <TextField
            label="Horizon (years)"
            value={horizonYears}
            onChange={(e) => setHorizonYears(e.target.value)}
            type="number"
            min={1}
          />
        </div>
      </Card>

      {result && (
        <>
          <Card className="mb-4">
            <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
              <div>
                Annual cap:{" "}
                <span className="font-mono font-semibold">
                  ${(result.cap_cents_per_year / 100).toLocaleString()}
                </span>{" "}
                <span className="text-[var(--color-muted)]">
                  ({numAdults} adults × $10,000 each)
                </span>
              </div>
              <div>
                Years to target:{" "}
                <span className="font-mono font-semibold">
                  {result.years_to_target}
                </span>
              </div>
              {projected !== null && (
                <div>
                  Projected at horizon:{" "}
                  <span className="font-mono font-semibold">
                    ${(projected / 100).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          </Card>
          <Card>
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-[var(--color-muted)]">
                <tr>
                  <th className="py-1">Year</th>
                  <th>Window</th>
                  <th>Purchase</th>
                  <th>Cumulative</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody className="font-mono tabular-nums">
                {result.rungs.map((r) => (
                  <tr key={r.year} className="border-t border-[var(--color-border-subtle)]">
                    <td className="py-1">{r.year}</td>
                    <td className="text-xs">{r.purchase_window}</td>
                    <td>${(r.purchase_cents / 100).toLocaleString()}</td>
                    <td>
                      ${(r.cumulative_principal_cents / 100).toLocaleString()}
                    </td>
                    <td className="text-xs text-[var(--color-muted)]">
                      {r.notes ?? ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}
